import React, { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog.js";
import { Button } from "./ui/button.js";
import { Badge } from "./ui/badge.js";
import { Search, Building2, UserPlus, MapPin, Briefcase, Users, Check } from "lucide-react";
import { formatRut } from "../../lib/rut.js";

export interface ClienteFrecuenteItem {
  id: number;
  userId: number;
  rutEmisor: string;
  rut: string;
  razonSocial: string;
  giro?: string | null;
  direccion?: string | null;
  comuna?: string | null;
  ciudad?: string | null;
  email?: string | null;
  telefono?: string | null;
  glosaHabitual?: string | null;
}

interface ClienteSelectorModalProps {
  open: boolean;
  clientes: ClienteFrecuenteItem[];
  currentRutEmisor?: string;
  onClose: () => void;
  onSelect: (cliente: ClienteFrecuenteItem) => void;
}

export default function ClienteSelectorModal({
  open,
  clientes,
  currentRutEmisor,
  onClose,
  onSelect,
}: ClienteSelectorModalProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showAllEmisores, setShowAllEmisores] = useState(false);

  // Filtrado por emisor y por texto de búsqueda
  const filteredClientes = useMemo(() => {
    let pool = clientes;

    if (currentRutEmisor && !showAllEmisores) {
      const cleanEmisor = currentRutEmisor.replace(/[.\s-]/g, "").toUpperCase();
      pool = pool.filter((c) => {
        const cEmisor = c.rutEmisor.replace(/[.\s-]/g, "").toUpperCase();
        return cEmisor === cleanEmisor;
      });
    }

    if (!searchTerm.trim()) return pool;

    const term = searchTerm.toLowerCase().trim();
    const cleanTermRut = term.replace(/[.\s-]/g, "");

    return pool.filter((c) => {
      const rutClean = c.rut.replace(/[.\s-]/g, "").toLowerCase();
      const razon = (c.razonSocial ?? "").toLowerCase();
      const giro = (c.giro ?? "").toLowerCase();
      const comuna = (c.comuna ?? "").toLowerCase();

      return (
        rutClean.includes(cleanTermRut) ||
        razon.includes(term) ||
        giro.includes(term) ||
        comuna.includes(term)
      );
    });
  }, [clientes, currentRutEmisor, showAllEmisores, searchTerm]);

  const hasOtherEmisores = useMemo(() => {
    if (!currentRutEmisor) return false;
    const cleanEmisor = currentRutEmisor.replace(/[.\s-]/g, "").toUpperCase();
    return clientes.some((c) => c.rutEmisor.replace(/[.\s-]/g, "").toUpperCase() !== cleanEmisor);
  }, [clientes, currentRutEmisor]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] flex flex-col p-6 gap-4">
        <DialogHeader className="pb-1">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-base font-semibold">Seleccionar Cliente Frecuente</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Inserta una nueva fila en la tabla con todos los datos del cliente cargados.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Barra de búsqueda y toggle de emisor */}
        <div className="flex flex-col gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Buscar por RUT, Razón Social, Giro o Comuna..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
              className="w-full rounded-md border border-input bg-background pl-9 pr-4 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            />
          </div>

          {currentRutEmisor && hasOtherEmisores && (
            <div className="flex items-center justify-between text-xs pt-1 px-1">
              <span className="text-muted-foreground">
                Filtrando por emisor: <strong className="text-foreground font-mono">{formatRut(currentRutEmisor)}</strong>
              </span>
              <button
                type="button"
                onClick={() => setShowAllEmisores(!showAllEmisores)}
                className="text-primary hover:underline font-medium"
              >
                {showAllEmisores ? "Solo emisor actual" : "Ver de otros emisores"}
              </button>
            </div>
          )}
        </div>

        {/* Lista de clientes */}
        <div className="flex-1 overflow-y-auto min-h-[220px] max-h-[360px] divide-y divide-border/60 rounded-md border border-border/80 bg-card">
          {filteredClientes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-center p-4">
              <Building2 className="h-8 w-8 text-muted-foreground/40 mb-2" />
              <p className="text-sm font-medium text-muted-foreground">No se encontraron clientes</p>
              <p className="text-xs text-muted-foreground/60 max-w-xs mt-1">
                {searchTerm
                  ? "Prueba buscando con otro término o activa ver de otros emisores."
                  : "Los clientes se aprenden automáticamente al emitir facturas o boletas."}
              </p>
            </div>
          ) : (
            filteredClientes.map((c) => {
              const isDifferentEmisor =
                currentRutEmisor &&
                c.rutEmisor.replace(/[.\s-]/g, "").toUpperCase() !==
                  currentRutEmisor.replace(/[.\s-]/g, "").toUpperCase();

              return (
                <div
                  key={c.id}
                  onClick={() => {
                    onSelect(c);
                    onClose();
                  }}
                  className="flex items-center justify-between p-3 hover:bg-accent/40 cursor-pointer transition-colors group"
                >
                  <div className="flex-1 min-w-0 pr-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-foreground truncate">
                        {c.razonSocial}
                      </span>
                      <span className="font-mono text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        {formatRut(c.rut)}
                      </span>
                      {isDifferentEmisor && (
                        <Badge variant="outline" className="text-[10px] py-0 border-amber-500/40 text-amber-600 dark:text-amber-400">
                          De emisor {formatRut(c.rutEmisor)}
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
                      {c.giro && (
                        <span className="flex items-center gap-1 truncate max-w-[200px]" title={c.giro}>
                          <Briefcase className="h-3 w-3 shrink-0" />
                          <span className="truncate">{c.giro}</span>
                        </span>
                      )}
                      {(c.direccion || c.comuna) && (
                        <span className="flex items-center gap-1 truncate max-w-[220px]" title={`${c.direccion ?? ""}, ${c.comuna ?? ""}`}>
                          <MapPin className="h-3 w-3 shrink-0" />
                          <span className="truncate">{[c.direccion, c.comuna].filter(Boolean).join(", ")}</span>
                        </span>
                      )}
                    </div>
                  </div>

                  <Button
                    size="sm"
                    variant="ghost"
                    className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 h-8 px-2.5 text-xs text-primary"
                  >
                    Insertar
                  </Button>
                </div>
              );
            })
          )}
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
          <span>{filteredClientes.length} cliente(s) disponible(s)</span>
          <Button variant="outline" size="sm" onClick={onClose} className="h-8">
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
